import { useFeatureFlag } from '@src/hooks';
import {
  abbreviateId,
  BlotterTable,
  copyText,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  IconName,
  useBehaviorSubject,
  useBlotterTable,
  useCallbackRef,
  type CptySettlementTypeEnum,
} from '@talos/kyoko';
import type { ColDef, GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useEffect, useMemo } from 'react';
import { map } from 'rxjs';
import type { ManualFillCareOrderEntry, ManualFillCareOrderLegEntry } from './manualFillCareOrdersSlice';
import {
  getManualFillConfirmationMessage,
  getManualFillConfirmationMessageCopyability,
} from './manualFillConfirmationMessage';
import { createCareOrderManualFillsFocusGridInnerElementHandler } from './manualFillInputFocus';
import { useCareOrderManualFillsBlotterColumns } from './useCareOrderManualFillsBlotterColumns';

interface ManualFillCareOrderBlotterRowValues {
  CptySettlementType?: CptySettlementTypeEnum;
  Symbol: string;
  Currency?: string;
  AmountCurrency: string;
  Counterparty?: string;
  initiatingFrom?: string;
  CumQty: string;
  LeavesQty: string;
}

interface ManualFillCareOrderParentBlotterRow extends ManualFillCareOrderEntry, ManualFillCareOrderBlotterRowValues {
  dataPath: string[];
  isLegEntry?: false;
}

interface ManualFillCareOrderLegBlotterRow extends ManualFillCareOrderLegEntry, ManualFillCareOrderBlotterRowValues {
  dataPath: string[];
  careOrderID: string;
  legSymbol: string;
  isLegEntry: true;
}

export type ManualFillCareOrderBlotterRow = ManualFillCareOrderParentBlotterRow | ManualFillCareOrderLegBlotterRow;

export interface CareOrderManualFillsBlotterExpansionControls {
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
}

interface CareOrderManualFillsBlotterProps {
  entries: ManualFillCareOrderEntry[];
  onEntryFillQtyChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillPriceChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillPriceReset: (careOrderID: string, legSymbol?: string) => void;
  onEntryFillAmountChange: (careOrderID: string, value: string | undefined, legSymbol?: string) => void;
  onEntryFillAmountReset: (careOrderID: string, legSymbol?: string) => void;
  onEntryRemove: (careOrderID: string) => void;
  onExpansionControlsChange?: (controls: CareOrderManualFillsBlotterExpansionControls) => void;
  showAllDecimals: boolean;
  isLoading: boolean;
}

function getDataPath(row: ManualFillCareOrderBlotterRow): string[] {
  return row.dataPath;
}

function getManualFillRowCareOrderID(row: ManualFillCareOrderBlotterRow | undefined) {
  if (!row) {
    return undefined;
  }

  return row.isLegEntry ? row.careOrderID : row.careOrder.OrderID;
}

export const CareOrderManualFillsBlotter = ({
  entries,
  onEntryFillQtyChange,
  onEntryFillPriceChange,
  onEntryFillPriceReset,
  onEntryFillAmountChange,
  onEntryFillAmountReset,
  onEntryRemove,
  onExpansionControlsChange,
  showAllDecimals,
  isLoading,
}: CareOrderManualFillsBlotterProps) => {
  const { enableCareOrderManualFillsConfirmationTextCopying } = useFeatureFlag();
  const rows = useMemo(() => entries.flatMap(entry => entryToBlotterRows(entry)), [entries]);
  const { observable: rowsObs } = useBehaviorSubject(() => rows, [rows]);
  const dataObservable = useMemo(
    () =>
      rowsObs.pipe(
        map(currentRows => ({
          initial: true,
          data: currentRows,
        }))
      ),
    [rowsObs]
  );

  const columns = useCareOrderManualFillsBlotterColumns({
    onEntryFillQtyChange,
    onEntryFillPriceChange,
    onEntryFillPriceReset,
    onEntryFillAmountChange,
    onEntryFillAmountReset,
    onEntryRemove,
    isLoading,
  });

  const autoGroupColumnDef = useMemo<ColDef<ManualFillCareOrderBlotterRow>>(
    () => ({
      headerName: 'Care Order ID',
      initialWidth: 100,
      valueGetter: params => getManualFillRowCareOrderID(params.data),
      valueFormatter: params => (params.value ? abbreviateId(params.value) : ''),
    }),
    []
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );
  const getCustomContextMenuItems = useCallbackRef(
    (params: GetContextMenuItemsParams<ManualFillCareOrderBlotterRow>) => {
      if (!enableCareOrderManualFillsConfirmationTextCopying) {
        return [];
      }

      const item = getCopyConfirmationMessagesMenuItem(params);
      return item ? [item] : [];
    }
  );

  const blotterTable = useBlotterTable<ManualFillCareOrderBlotterRow>({
    dataObservable,
    errorObservable: undefined,
    rowID: 'rowID' satisfies keyof ManualFillCareOrderBlotterRow,
    applyTransactionsSynchronously: true, // we are editing stuff in the blotter, so synchronous updates make it smoother
    settings: blotterTableSettings,
    gridOptions: {
      treeData: true,
      getDataPath,
      autoGroupColumnDef,
      suppressGroupRowsSticky: true,
      rowHeight: 40, // I want to have a less dense grid, but without the padding that comes with so I can save a bit of width. So setting rowHeight explicitly.
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      suppressHeaderFocus: true,
      focusGridInnerElement: createCareOrderManualFillsFocusGridInnerElementHandler(),
    },
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    columns,
  });

  const expansionControls = useMemo(
    () => ({
      expandAllGroups: blotterTable.expandAllGroups,
      collapseAllGroups: blotterTable.collapseAllGroups,
    }),
    [blotterTable.collapseAllGroups, blotterTable.expandAllGroups]
  );

  useEffect(() => {
    onExpansionControlsChange?.(expansionControls);
  }, [expansionControls, onExpansionControlsChange]);

  return <BlotterTable {...blotterTable} background="backgroundModal" />;
};

function getCopyConfirmationMessagesMenuItem(
  params: GetContextMenuItemsParams<ManualFillCareOrderBlotterRow>
): MenuItemDef | undefined {
  const entriesToCopy = getManualFillEntriesToCopy(params);
  if (entriesToCopy.length === 0) {
    return undefined;
  }

  const name =
    entriesToCopy.length === 1 ? 'Copy confirmation message' : `Copy confirmation messages (${entriesToCopy.length})`;

  // See if any selected row is not copyable, and if so, disable the button and show the disablement reason to the user as a tooltip
  const notCopyable = entriesToCopy.map(getManualFillConfirmationMessageCopyability).find(result => !result.canCopy);
  if (notCopyable) {
    return {
      name,
      icon: `<i class="ag-icon ${IconName.ClipboardCopy}"/>`,
      disabled: true,
      tooltip: notCopyable.reason,
    };
  }

  // Else, the user can click the button to copy the message(s) to their clipboard
  const messages = entriesToCopy
    .map(entry => getManualFillConfirmationMessage(entry))
    .filter((message): message is string => message != null);

  return {
    name,
    icon: `<i class="ag-icon ${IconName.ClipboardCopy}"/>`,
    action: () => copyText(messages.join('\n')),
  };
}

function getManualFillEntriesToCopy(
  params: GetContextMenuItemsParams<ManualFillCareOrderBlotterRow>
): ManualFillCareOrderEntry[] {
  const selectedEntries = params.api
    .getSelectedNodes()
    .map(node => node.data)
    .filter(isManualFillCareOrderParentRow);

  if (selectedEntries.length > 0) {
    return selectedEntries;
  }

  return isManualFillCareOrderParentRow(params.node?.data) ? [params.node.data] : [];
}

function isManualFillCareOrderParentRow(
  row: ManualFillCareOrderBlotterRow | undefined
): row is ManualFillCareOrderParentBlotterRow {
  // Just make sure we only get the parent (care order) rows
  return row != null && !row.isLegEntry;
}

function entryToBlotterRows(entry: ManualFillCareOrderEntry): ManualFillCareOrderBlotterRow[] {
  const parentRow: ManualFillCareOrderBlotterRow = {
    ...entry,
    CptySettlementType: entry.careOrder.CptySettlementType,
    Symbol: entry.careOrder.Symbol,
    Currency: entry.careOrder.Currency,
    AmountCurrency: entry.careOrder.AmountCurrency,
    Counterparty: entry.careOrder.Counterparty,
    initiatingFrom: entry.careOrder.initiatingFrom,
    CumQty: entry.careOrder.CumQty,
    LeavesQty: entry.careOrder.LeavesQty,
    dataPath: [entry.careOrder.OrderID],
    isLegEntry: false,
  };
  const legRows =
    entry.legEntries?.map(legEntry => ({
      ...legEntry,
      CptySettlementType: entry.careOrder.CptySettlementType,
      Symbol: legEntry.legSummary.Symbol,
      Currency: legEntry.legSummary.Currency,
      AmountCurrency: legEntry.legSummary.AmountCurrency,
      Counterparty: undefined,
      initiatingFrom: undefined,
      CumQty: legEntry.legSummary.CumQty,
      LeavesQty: legEntry.legSummary.LeavesQty,
      dataPath: [entry.careOrder.OrderID, legEntry.legSummary.Symbol],
      careOrderID: entry.careOrder.OrderID,
      legSymbol: legEntry.legSummary.Symbol,
      isLegEntry: true as const,
    })) ?? [];

  return [parentRow, ...legRows];
}
